import { useState, useEffect, KeyboardEvent as ReactKeyboardEvent } from "react";
import {
  useFormContext,
  FieldError,
  Merge,
  FieldErrorsImpl,
  ControllerRenderProps,
  FieldValues,
} from "react-hook-form";

import { useThemePreviewContext } from "containers/admin/clients/client/theme-page/contexts/theme-preview-context";
import ColorSwatch from "containers/admin/clients/client/theme-page/preview/components/color-swatch/color-swatch";

import { ButtonKeys } from "containers/admin/clients/client/theme-page/preview/preview-sidebar/preview-sidebar.types";
import {
  AppButtonFormData,
  CustomSelectOption,
  ButtonSectionKeys,
} from "containers/admin/clients/client/theme-page/preview/preview.types";

import {
  BG_COLOR,
  // BORDER_RADIUS,
  BORDER_STYLE,
  CUSTOM_VALUE,
  WIDTH_VALUE,
  NONE_VALUE,
  COLOR_VALUE,
  NAME_VALUE,
} from "containers/admin/clients/client/theme-page/preview/sub-tabs/button/button.constants";
import { VALID_FORMAT_MESSAGE } from "containers/admin/clients/client/theme-page/preview/preview.constants";
import THEME_DEFAULT_DATA from "containers/admin/clients/client/theme-page/theme-default-data.json";

import Icons from "assets";

export const useButtonContent = () => {
  // default values
  const defaultButton = THEME_DEFAULT_DATA.button;

  // modal state
  const [isBorderRadiusCardOpen, setIsBorderRadiusCardOpen] = useState(false);
  // context
  const { isEditMode, colors, typography, button, setButton } = useThemePreviewContext();

  //form
  const {
    clearErrors,
    formState: { errors },
    getValues,
    setValue,
    setError,
    watch,
  } = useFormContext();

  // errors
  const buttonErrors = errors?.button as Record<
    string,
    FieldError | Merge<FieldError, FieldErrorsImpl<AppButtonFormData>>
  >;

  const getSectionErrors = (field: ButtonKeys) => {
    return buttonErrors?.[field] as Record<
      string,
      FieldError | Merge<FieldError, FieldErrorsImpl<AppButtonFormData>>
    >;
  };

  const isThemeSet = typography && colors && button;

  // helper to get value for selects
  const getSelectValue = (
    name: keyof AppButtonFormData,
    field: CustomSelectOption,
    isBorderColor: boolean,
  ) => {
    return (
      isThemeSet && {
        value: field?.value,
        label: (
          <div className="flex gap-1 relative w-full">
            {(name === BG_COLOR || isBorderColor) && <ColorSwatch color={colors[field?.value]!} />}
            {field?.label}
            {((name === BG_COLOR && button?.backgroundColor?.name?.isAlertShown) ||
              (isBorderColor && button?.borderStyle?.color?.isAlertShown)) && (
              <Icons.ExclamationCircle className="overflow-visible absolute text-critical-700 -bottom-0.5 size-6 left-[180px]" />
            )}
          </div>
        ),
      }
    );
  };

  // helper to update border width with arrows
  const arrowHelper = (field: ControllerRenderProps<FieldValues, string>, num: number) => {
    const newValue = Math.max(0, Number(field.value) + num);
    handleCustomChange(String(newValue), BORDER_STYLE, WIDTH_VALUE, field.onChange);
  };

  // helper to reset custom inputs
  const resetCustomVals = (
    section: ButtonKeys,
    value: string | CustomSelectOption,
    field: ButtonSectionKeys,
  ) => {
    setValue(`button.${section}.${field}`, value);
    clearErrors(`button.${section}.${field}`);

    setButton((prev: AppButtonFormData | null) => {
      if (!prev) return prev;
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      };
    });
  };

  // handle change for all the basic level select inputs
  const handleChange = (
    section: ButtonKeys,
    input: CustomSelectOption,
    field: ButtonSectionKeys,
    fieldOnChange: (value: CustomSelectOption) => void,
  ) => {
    // getting which values to save based on section
    const { label, value, isAlertShown } = input;
    const isColorInput =
      (section === BG_COLOR && isAlertShown) ||
      (section === BORDER_STYLE && field === COLOR_VALUE && isAlertShown);
    const inputValue = isColorInput ? { label, value, isAlertShown } : { label, value };

    setButton((prev: AppButtonFormData | null) => {
      if (!prev) return prev;

      // set context based on structure of input
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: inputValue,
        },
      };
    });

    // reset custom values to defaults when we click off custom
    if (section === BG_COLOR && value !== CUSTOM_VALUE) {
      resetCustomVals(section, defaultButton.backgroundColor.custom, CUSTOM_VALUE);
    }

    //HIDDEN till v2
    // if (section === BORDER_RADIUS && value !== CUSTOM_VALUE) {
    //   resetCustomVals(section, defaultButton.borderRadius.custom, CUSTOM_VALUE);
    // }

    if (section === BORDER_STYLE && field === COLOR_VALUE && value !== CUSTOM_VALUE) {
      resetCustomVals(section, defaultButton.borderStyle.custom, CUSTOM_VALUE);
    }

    if (section === BORDER_STYLE && field === NAME_VALUE && value === NONE_VALUE) {
      resetCustomVals(section, defaultButton.borderStyle.color, COLOR_VALUE);
      resetCustomVals(section, defaultButton.borderStyle.custom, CUSTOM_VALUE);
      resetCustomVals(section, defaultButton.borderStyle.width, WIDTH_VALUE);
    }

    fieldOnChange({ label, value });
  };

  // handle change for custom inputs
  const handleCustomChange = (
    value: string,
    section: ButtonKeys,
    field: string,
    fieldOnChange: (value: string) => void,
  ) => {
    const hasError = getSectionErrors(section)?.[field];

    if (value !== "#" && !hasError) {
      setButton((prev: AppButtonFormData | null) => {
        if (!prev) return prev;

        // set context based on targeted field
        return field === WIDTH_VALUE
          ? {
              ...prev,
              [section]: {
                ...prev[section],
                width: value,
              },
            }
          : {
              ...prev,
              [section]: {
                ...prev[section],
                custom: value,
              },
            };
      });
    }

    fieldOnChange(value);
  };

  // set input to custom and open modal when clicking on border radius button
  const handleRadiusBtnClick = (
    name: ButtonKeys,
    field: ControllerRenderProps<FieldValues, string>,
  ) => {
    handleChange(name, { label: "Custom...", value: CUSTOM_VALUE }, NAME_VALUE, field.onChange);
    setIsBorderRadiusCardOpen((prev) => !prev);
  };

  // restrict input for borderStyle width field
  const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>, value: string) => {
    // do not allow these in number field
    if (["e", "E", ".", "-", "+"].includes(event.key)) {
      event.preventDefault();
    }

    // only allow ArrowDown if value > 0
    if (event.key === "ArrowDown" && Number(value) <= 0) {
      event.preventDefault();
    }
  };

  // close border modal when not in edit mode
  useEffect(() => {
    if (!isEditMode) {
      setIsBorderRadiusCardOpen(false);
    }
  }, [isEditMode]);

  // setting errors for custom color on selecting custom and empty custom color
  const backgroundColorName =
    watch(`button.${BG_COLOR}.name`)?.value === CUSTOM_VALUE &&
    getValues(`button.${BG_COLOR}.custom`) === "";

  const borderColorName =
    watch(`button.${BORDER_STYLE}.color`)?.value === CUSTOM_VALUE &&
    getValues(`button.${BORDER_STYLE}.custom`) === "";

  useEffect(() => {
    if (backgroundColorName) {
      setError(`button.${BG_COLOR}.custom`, {
        type: CUSTOM_VALUE,
        message: VALID_FORMAT_MESSAGE,
      });
    }
    if (borderColorName) {
      setError(`button.${BORDER_STYLE}.custom`, {
        type: CUSTOM_VALUE,
        message: VALID_FORMAT_MESSAGE,
      });
    }
  }, [backgroundColorName, borderColorName, setError]);

  return {
    getSelectValue,
    arrowHelper,
    isBorderRadiusCardOpen,
    handleRadiusBtnClick,
    setIsBorderRadiusCardOpen,
    getSectionErrors,
    handleCustomChange,
    handleChange,
    handleKeyDown,
  };
};
